/** A header the dragged column can land beside, in content coordinates. */
export interface ColumnSlot {
    id: string;
    start: number;
    end: number;
}

/** Where a dragged column lands: before a column, or after the last slot. */
export interface ColumnDropTarget {
    beforeColumnId: string | null;
    /** The gap's x in content coordinates, for the indicator. */
    x: number;
}

/**
 * The gap nearest the pointer among the source's sibling headers (same pin
 * zone and group), or null when that gap is on either side of the source.
 */
export const getColumnDropTarget = ({
    slots,
    sourceId,
    x,
}: {
    slots: readonly ColumnSlot[];
    sourceId: string;
    x: number;
}): ColumnDropTarget | null => {
    const sourceIndex = slots.findIndex((slot) => slot.id === sourceId);
    const found = slots.findIndex((slot) => x < (slot.start + slot.end) / 2);
    const gap = found < 0 ? slots.length : found;
    const last = slots[slots.length - 1];
    if (sourceIndex < 0 || gap === sourceIndex || gap === sourceIndex + 1) {
        return null;
    }
    const before = slots[gap];
    return {
        beforeColumnId: before?.id ?? null,
        x: before?.start ?? last?.end ?? 0,
    };
};

/**
 * The `toIndex` for `moveColumn`: the position among the zone's value
 * columns (hidden ones included) once the source is taken out.
 */
export const getColumnMoveIndex = ({
    zoneColumnIds,
    sourceId,
    target,
    lastSlotId,
}: {
    zoneColumnIds: readonly string[];
    sourceId: string;
    target: ColumnDropTarget;
    lastSlotId: string;
}) => {
    const rest = zoneColumnIds.filter((id) => id !== sourceId);
    return target.beforeColumnId === null
        ? rest.indexOf(lastSlotId) + 1
        : rest.indexOf(target.beforeColumnId);
};
